import { z } from 'zod';
import { todayInTimezone } from '../dates';
import { BANK_ACCOUNT_TYPES } from '../enums';
import { emptyAsUndefined, optionalText, text, ymd } from './common';

const notFuture = (v: string | undefined) => v === undefined || v <= todayInTimezone();

/**
 * Indian bank account that receives a revenue account's payouts (AdMob, AdX…).
 * Only the bank name and account number are kept. Changing it keeps history:
 * the previous link ends the day before the new one starts.
 */
const bankDetails = {
  accountHolderName: text(150, 'Account holder name'),
  accountType: z.enum(BANK_ACCOUNT_TYPES, { error: 'Choose the account type' }),
};

export const bankLinkSchema = z.object({
  bankName: text(100, 'Bank name'),
  ...bankDetails,
  /** Indian account numbers are 9–18 digits. */
  accountNumber: z
    .string({ error: 'Account number is required' })
    .transform((v) => v.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{9,18}$/, 'Enter a 9–18 digit account number')),
  /** First day payouts go to this account (default today). */
  linkedOn: emptyAsUndefined(ymd).refine(notFuture, 'Date cannot be in the future'),
  notes: optionalText(500),
});
export type BankLinkInput = z.output<typeof bankLinkSchema>;

/** Correct the holder / type of the current link (bank and number need a new link). */
export const bankDetailsSchema = z.object(bankDetails);
export type BankDetailsInput = z.output<typeof bankDetailsSchema>;

export const bankUnlinkSchema = z.object({
  /** Last day payouts went to this account (default today). */
  unlinkedOn: emptyAsUndefined(ymd).refine(notFuture, 'Date cannot be in the future'),
});
export type BankUnlinkInput = z.output<typeof bankUnlinkSchema>;

/** '012345678901' → '••8901' */
export const maskAccountNumber = (n: string) => `••${n.slice(-4)}`;
